import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    ChevronLeft,
    ChevronRight,
    Search,
    Star,
    Users,
    MapPin,
    School,
    TrendingUp,
    TrendingDown,
    Award,
    Building,
    GraduationCap
} from 'lucide-react';
import StarRating from './StarRating';
import studentCountCache from '../utils/studentCountCache';
import schoolStatsCache from '../utils/schoolStatsCache';

const RegionSchoolsTable = ({ schools }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('studentCount');
    const [sortOrder, setSortOrder] = useState('desc');
    const [activeRegion, setActiveRegion] = useState('all');
    const [filteredSchools, setFilteredSchools] = useState([]);
    const [schoolCounts, setSchoolCounts] = useState({});
    const [schoolStats, setSchoolStats] = useState({});
    const [loading, setLoading] = useState(true);

    const itemsPerPage = 10;

    const regions = [
        { id: 'all', name: 'All Regions', icon: MapPin, color: '#6B7280' },
        { id: 'Greater Banjul Area', name: 'Greater Banjul Area', icon: Building, color: '#3B82F6' },
        { id: 'West Coast Region', name: 'West Coast Region', icon: MapPin, color: '#10B981' },
        { id: 'North Bank Region', name: 'North Bank Region', icon: School, color: '#F59E0B' },
        { id: 'Central River Region', name: 'Central River Region', icon: GraduationCap, color: '#8B5CF6' },
        { id: 'Upper River Region', name: 'Upper River Region', icon: Award, color: '#EF4444' },
        { id: 'Lower River Region', name: 'Lower River Region', icon: Users, color: '#06B6D4' }
    ];

    // Load student counts and stats
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Load student counts
                const counts = await studentCountCache.getStudentCounts();
                const countsMap = {};
                counts.forEach(item => {
                    countsMap[item.schoolId] = item.studentCount;
                });
                setSchoolCounts(countsMap);

                // Load stats for visible schools
                const visibleSchools = schools.slice(0, 50); // Load stats for first 50 schools
                const statsMap = {};
                for (const school of visibleSchools) {
                    const cachedStats = schoolStatsCache.get(school.id);
                    if (cachedStats) {
                        statsMap[school.id] = cachedStats;
                    }
                }
                setSchoolStats(statsMap);
            } catch (error) {
                console.error('Error loading school data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [schools]);

    // Filter and sort schools
    useEffect(() => {
        let filtered = [...schools];

        // Apply region filter
        if (activeRegion !== 'all') {
            filtered = filtered.filter(school => school.region === activeRegion);
        }

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(school =>
                school.name.toLowerCase().includes(query) ||
                school.district.toLowerCase().includes(query) ||
                school.region.toLowerCase().includes(query)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'studentCount':
                    aValue = schoolCounts[a.id] || 0;
                    bValue = schoolCounts[b.id] || 0;
                    break;
                case 'rating':
                    aValue = schoolStats[a.id]?.averageRating || 0;
                    bValue = schoolStats[b.id]?.averageRating || 0;
                    break;
                case 'reviewCount':
                    aValue = schoolStats[a.id]?.reviewCount || 0;
                    bValue = schoolStats[b.id]?.reviewCount || 0;
                    break;
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'region':
                    aValue = a.region.toLowerCase();
                    bValue = b.region.toLowerCase();
                    break;
                default:
                    aValue = a[sortBy] || 0;
                    bValue = b[sortBy] || 0;
            }

            if (typeof aValue === 'string') {
                return sortOrder === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
            }
        });

        setFilteredSchools(filtered);
        setCurrentPage(1); // Reset to first page when filtering
    }, [schools, searchQuery, sortBy, sortOrder, schoolCounts, schoolStats, activeRegion]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    const totalPages = Math.ceil(filteredSchools.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSchools = filteredSchools.slice(startIndex, endIndex);

    const getSortIcon = (column) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? <TrendingUp size={16} /> : <TrendingDown size={16} />;
    };

    const getRegionColor = (region) => {
        const regionColors = {
            'Greater Banjul Area': '#3B82F6',
            'West Coast Region': '#10B981',
            'North Bank Region': '#F59E0B',
            'Central River Region': '#8B5CF6',
            'Upper River Region': '#EF4444',
            'Lower River Region': '#06B6D4'
        };
        return regionColors[region] || '#6B7280';
    };

    const getSchoolTypeIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'primary': return <School size={16} className="text-blue-600" />;
            case 'secondary': return <GraduationCap size={16} className="text-green-600" />;
            case 'basic': return <Building size={16} className="text-purple-600" />;
            default: return <School size={16} className="text-gray-600" />;
        }
    };

    const getRankingBadge = (rank) => {
        if (rank === 1) return <Award size={16} className="text-yellow-500" />;
        if (rank === 2) return <Award size={16} className="text-gray-400" />;
        if (rank === 3) return <Award size={16} className="text-orange-500" />;
        return <span className="text-sm font-bold text-gray-600">#{rank}</span>;
    };

    return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Schools Directory</h2>
                        <p className="text-gray-600 mt-1">
                            {filteredSchools.length} schools • Ranked by student enrollment
                        </p>
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search schools..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
                        />
                    </div>
                </div>
            </div>

            {/* Region Tabs */}
            <div className="border-b border-gray-200">
                <div className="flex flex-wrap gap-2 p-4">
                    {regions.map((region) => {
                        const Icon = region.icon;
                        const isActive = activeRegion === region.id;

                        return (
                            <button
                                key={region.id}
                                onClick={() => setActiveRegion(region.id)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                                        ? 'text-white shadow-md'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                style={{
                                    backgroundColor: isActive ? region.color : 'transparent'
                                }}
                            >
                                <Icon size={16} />
                                <span className="text-sm">{region.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('studentCount')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Rank</span>
                                    {getSortIcon('studentCount')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>School</span>
                                    {getSortIcon('name')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('region')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Location</span>
                                    {getSortIcon('region')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('studentCount')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Students</span>
                                    {getSortIcon('studentCount')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('rating')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Rating</span>
                                    {getSortIcon('rating')}
                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('reviewCount')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Reviews</span>
                                    {getSortIcon('reviewCount')}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-12 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                                    <p className="mt-2 text-gray-500">Loading schools...</p>
                                </td>
                            </tr>
                        ) : currentSchools.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-12 text-center">
                                    <School size={48} className="text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">No schools found matching your search.</p>
                                </td>
                            </tr>
                        ) : (
                            currentSchools.map((school, index) => {
                                const rank = startIndex + index + 1;
                                const studentCount = schoolCounts[school.id] || 0;
                                const stats = schoolStats[school.id] || { averageRating: 0, reviewCount: 0 };

                                return (
                                    <motion.tr
                                        key={school.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                                    {getRankingBadge(rank)}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0 mr-3"
                                                    style={{ backgroundColor: getRegionColor(school.region) }}
                                                />
                                                <div className="flex items-center space-x-2">
                                                    {getSchoolTypeIcon(school.type)}
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {school.name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {school.type} • {school.management}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <MapPin size={14} className="text-gray-400 mr-1" />
                                                <span>{school.district}, {school.region}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <Users size={14} className="text-primary-600 mr-1" />
                                                <span className="font-medium">{studentCount}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <StarRating
                                                    rating={stats.averageRating}
                                                    size={16}
                                                    showNumber={true}
                                                    showCount={false}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {stats.reviewCount} {stats.reviewCount === 1 ? 'review' : 'reviews'}
                                        </td>
                                    </motion.tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredSchools.length)} of {filteredSchools.length} schools
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <div className="flex items-center space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                const page = i + 1;
                                return (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-3 py-1 rounded-lg text-sm ${currentPage === page
                                                ? 'bg-primary-600 text-white'
                                                : 'text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegionSchoolsTable;
